import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import * as z from 'zod'

import { useUpdateInvite } from 'src/features/invites/api/use-update-invite'
import { Button } from 'src/shared/components/ui/buttons/button'
import { Form } from 'src/shared/components/ui/forms/form'
import { InputFormField } from 'src/shared/components/ui/forms/form-fields/input'
import { useToast } from 'src/shared/hooks/use-toast'
import type { Invite } from 'src/shared/schemas/invites/invite'
import { cn } from 'src/shared/utils/cn'

const updateSchema = z.object({
  phone: z.string().length(14, 'Phone number must be 10 numbers'),
})

type UpdateFormData = z.infer<typeof updateSchema>

interface UpdateInviteFormProps {
  invite: Invite
  onSuccess?: () => void
  className?: string
}

export const UpdateInviteForm = ({ invite, onSuccess, className }: UpdateInviteFormProps) => {
  const { toast } = useToast()
  const { mutate: updateInvite, isPending } = useUpdateInvite()

  const form = useForm<UpdateFormData>({
    resolver: zodResolver(updateSchema),
    defaultValues: {
      phone: invite.invitedPhone || '',
    },
  })

  const onSubmit = (data: UpdateFormData) => {
    updateInvite(
      { inviteId: invite.id, data: { invitedPhone: data.phone } },
      {
        onSuccess: () => {
          toast({
            title: 'Phone number updated',
            description: `Phone number has been added to the invite for ${invite.invitedEmail}`,
          })
          form.reset()
          onSuccess?.()
        },
        onError: () => {
          toast({
            variant: 'destructive',
            title: 'Failed to update phone number',
            description: 'Please try again later',
          })
        },
      },
    )
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className={cn(className, 'flex flex-col justify-between gap-4')}>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <h3 className="text-2xl font-bold">Add phone number</h3>
            <p className="text-start text-sm text-muted">Your invitees will get a text!</p>
          </div>

          <div className="space-y-1">
            <InputFormField
              control={form.control}
              name="phone"
              label="Phone number"
              type="tel"
              disabled={isPending}
              className="w-full"
            />
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <Button type="submit" disabled={!form.watch().phone} isLoading={isPending} className="w-full">
            Update Phone
          </Button>
        </div>
      </form>
    </Form>
  )
}
